import { useEffect, useState } from "react";
import { Button, Card, CardContent, CardHeader, CardTitle, ConfirmDialog, LoadingState } from "@wa/ui";
import { Link2 } from "lucide-react";
import { useT, useI18n } from "../../../i18n/I18nProvider";
import { ApiError } from "../../../lib/api";
import { listConnectedAccounts, unlinkConnectedAccount, type ConnectedAccount, type AccountUser } from "../../../lib/auth";
import { fmt } from "./lib";

// ── Linked accounts (Google and other OAuth credentials) ─────────────────────
function providerLabel(provider: string): string {
  return provider === "google" ? "Google" : provider.charAt(0).toUpperCase() + provider.slice(1);
}

/** Google's colored G logo (missing from lucide; the official multicolor mark). */
function GoogleMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81Z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.93-2.91l-3.87-3a7.24 7.24 0 0 1-10.8-3.8H1.27v3.1A12 12 0 0 0 12 24Z" />
      <path fill="#FBBC05" d="M5.26 14.29a7.2 7.2 0 0 1 0-4.58v-3.1H1.27a12 12 0 0 0 0 10.78l3.99-3.1Z" />
      <path fill="#EA4335" d="M12 4.77c1.77 0 3.35.61 4.6 1.8l3.43-3.43A11.98 11.98 0 0 0 1.27 6.6l3.99 3.1A7.16 7.16 0 0 1 12 4.77Z" />
    </svg>
  );
}

export function ConnectedAccountsCard({ user }: { user: AccountUser }) {
  const t = useT();
  const { locale } = useI18n();
  const [items, setItems] = useState<ConnectedAccount[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The account stays set while the dialog closes, so the provider name in its
  // title does not blank out during the fade.
  const [confirming, setConfirming] = useState<ConnectedAccount | null>(null);
  const [open, setOpen] = useState(false);

  async function load() {
    try {
      setItems(await listConnectedAccounts());
    } catch {
      setItems([]);
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function unlink() {
    const a = confirming;
    if (!a) return;
    setBusyId(a.id);
    setError(null);
    try {
      await unlinkConnectedAccount(a.id);
      await load();
    } catch (err) {
      const code = err instanceof ApiError ? (err.body as { error?: string })?.error : null;
      setError(code === "last_login_method" ? t("security.connected.lastMethod") : t("common.error"));
    } finally {
      setBusyId(null);
      setOpen(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <Link2 className="size-5 text-primary" />
          <div className="flex-1">
            <CardTitle>{t("security.connected.title")}</CardTitle>
            <p className="text-sm text-muted-foreground">{t("security.connected.hint")}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
        {items === null ? (
          <LoadingState variant="inline" label={t("common.loading")} />
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("security.connected.empty")}</p>
        ) : (
          <>
            <ul className="space-y-2">
              {items.map((a) => (
                <li key={a.id} className="flex items-center gap-3 rounded-md border bg-card p-3">
                  {a.provider === "google" ? (
                    <GoogleMark className="size-4 shrink-0" />
                  ) : (
                    <Link2 className="size-4 shrink-0 text-muted-foreground" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <span>{providerLabel(a.provider)}</span>
                      {a.email && <span className="truncate text-muted-foreground font-normal">{a.email}</span>}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {t("security.connected.linkedOn", { date: fmt(a.createdAt, locale) })}
                      {a.lastUsedAt ? ` · ${t("security.connected.lastUsed", { date: fmt(a.lastUsedAt, locale) })}` : ""}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={busyId === a.id}
                    onClick={() => {
                      setConfirming(a);
                      setOpen(true);
                    }}
                  >
                    {t("security.connected.unlink")}
                  </Button>
                </li>
              ))}
            </ul>
            {!user.hasPassword && (
              <p className="text-xs text-muted-foreground">{t("security.connected.noPasswordHint")}</p>
            )}
          </>
        )}
      </CardContent>

      <ConfirmDialog
        open={open}
        variant="destructive"
        title={t("security.connected.confirmUnlink", { provider: confirming ? providerLabel(confirming.provider) : "" })}
        confirmLabel={t("security.connected.unlink")}
        cancelLabel={t("common.cancel")}
        loading={busyId !== null}
        onConfirm={() => void unlink()}
        onCancel={() => setOpen(false)}
      />
    </Card>
  );
}
